import { Injectable } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { BehaviorSubject, Observable } from "rxjs";
import {
  Rule,
  PredicateFilter,
  ReturnResponse,
  REMethodNames,
  REMethodParameters,
} from "../rule-config-component/models/rule-engine";
import { HttpClient } from "@angular/common/http";
import { AppConfigService } from "../configurations/app-config.service";

@Injectable({
  providedIn: "root",
})
export class RuleEngineService {
  private _rulesSubject = new BehaviorSubject<any>(null);
  private _pathSubject = new BehaviorSubject<any>(null);
  private _isExpressionBuilderPopUpSubject = new BehaviorSubject<boolean>(
    false
  );
  private rules: Rule[] = [];
  conditionsAdded: boolean = false;
  oldString: string = "";
  dataToSendForPredicatePopUp: any;
  holdTheBuildExpressionData: any;
  clearXpath: boolean = false;
  clearPredicateData: boolean = false;
  // dataFromThePredicatePopUp: any;
  constructor(private _fb: FormBuilder, private _httpClient : HttpClient) {}
  //behavior subject used for file name
  private fileNameSubject = new BehaviorSubject<string>("");
  private popUpSubject = new BehaviorSubject<boolean>(false);
  private subConditionsSubject = new BehaviorSubject<string[]>([]);
  private updateFieldWithPredicateConditionsSubject =
    new BehaviorSubject<string>("");
  private finalRuleExpressionSubject = new BehaviorSubject<string>("");
  protected apiURL: string = AppConfigService.settings.WebApiURL;
  public header = { withCredentials: true }


  getTheBuilderExpressionData() {
    return this.holdTheBuildExpressionData;
  }

  $getUpdatedFieldWithPredicate =
    this.updateFieldWithPredicateConditionsSubject.asObservable();
  setFieldWithUpdatedPredicate(dataFromThePredicatePopUp: any) {
    if (!this.clearXpath) {
      let fullyUpdatedPathWithConditions = this.pathWithPredicateFilters(
        dataFromThePredicatePopUp,
        this.oldString
      );
      this.updateFieldWithPredicateConditionsSubject.next(
        fullyUpdatedPathWithConditions
      );
    } else {
      this.updateFieldWithPredicateConditionsSubject.next(
        dataFromThePredicatePopUp
      );
      this.clearXpath = false;
    }
  }

  //setting the final rule so that it will be displayed in the main screen
  setFinalRuleExpression(finalRuleExpression: string) {
    this.finalRuleExpressionSubject.next(finalRuleExpression);
  }

  //get the final rule expression to show in the main screen
  $getFinalRuleExpression = this.finalRuleExpressionSubject.asObservable();

  //updating the behavior subject with new value
  setFileName(fileName: string) {
    this.fileNameSubject.next(fileName);
  }

  //returning the observable to subscribe it to get the latest value
  getFileName() {
    return this.fileNameSubject.asObservable();
  }

  $getPopUpValue = this.popUpSubject.asObservable();
  setPopUpValue(isPopUp: boolean) {
    this.popUpSubject.next(isPopUp);
  }

  $getSubConditionsFor = this.subConditionsSubject.asObservable();

  setSubConditionsFor(field: any, nodesWithLeafNodes: any, nodePaths: any) {
    if (!this.clearPredicateData) {
      // if (nodePaths.includes(field) && this.oldString != field) {
      //   this.oldString = field;
      //   this.dataToSendForPredicatePopUp = this.generateDataForPredicate(
      //     field,
      //     nodesWithLeafNodes
      //   );
      //   this.subConditionsSubject.next(this.dataToSendForPredicatePopUp);
      // } else {
      //here we have to parse the data from the string to form array json and then assign it to "dataToSendForPredicatePopUp" variable
      //write a method to parse the data from the incoming string into form json array. assign the return value of this to the
      this.oldString = field;
      this.subConditionsSubject.next(this.dataToSendForPredicatePopUp);
      // }
    } else {
      this.subConditionsSubject.next([]);
      this.clearPredicateData = false;
    }
  }

  private generatePaths(paths: any): string[] {
    if (!paths || !Array.isArray(paths) || paths.length === 0) {
      return []; // Return an empty array if input is invalid
    }

    let result: string[] = [];
    let currentPath = "";

    for (const path of paths) {
      currentPath = currentPath ? `${currentPath}/${path}` : path;
      result.push(currentPath);
    }

    return result;
  }

  addGroup() {
    return this._fb.group({
      logicalOperator: this._fb.control("AND", Validators.required), // Default to 'AND'
      conditions: this._fb.array([this.createConditionGroup()]), // Start with one empty condition
    });
  }

  createConditionGroup(): FormGroup {
    return this._fb.group({
      attribute: this._fb.control("", Validators.required),
      condition: this._fb.control("", Validators.required),
      value: this._fb.control("", Validators.required),
    });
  }

  pathWithPredicateFilters(json: any[], originalPath: string): string {
    let valuesFromOriginalPath = originalPath.split("/");
    let finalPathToReturn = valuesFromOriginalPath.map(
      (originalNodeName: any) => {
        let predicateNode = json.find(
          (node: any) => node.nodeName === originalNodeName
        );
        if (predicateNode) {
          let predicateCondition = this.generateThePath(predicateNode);
          if (predicateCondition != "") {
            return `${originalNodeName}[${predicateCondition}]`;
          } else {
            return `${originalNodeName}`;
          }
        } else {
          return originalNodeName;
        }
      }
    );
    return finalPathToReturn.join("/");
  }
  generateThePath(predicateNode: any): string {
    const groups = predicateNode.formGroup.get("groupArray") as FormArray;

    return (groups.controls as FormGroup[])
      .map((group: FormGroup, groupIndex: number) => {
        const conditions = group.get("conditions") as FormArray;

        const groupConditions = (conditions.controls as FormControl[])
          .map((condition: FormControl, conditionIndex: number) => {
            // Build individual condition string
            return `${condition.get("attribute")?.value}${
              condition.get("condition")?.value
            }'${condition.get("value")?.value}'`;
          })
          .join(` ${group.get("logicalOperator")?.value} `); // Join conditions within a group

        // Wrap group conditions in parentheses if there are multiple groups or multiple conditions
        return conditions.length > 1 || groups.length > 1
          ? `(${groupConditions})`
          : groupConditions;
      })
      .join(" AND "); // Join groups with 'AND'
  }
  getAllRules() {
    return this._rulesSubject.asObservable();
  }
  setAllTheRules(rules: any) {
    this.rules = [...rules];
    this._rulesSubject.next(this.rules);
  }
  clearAllTheRules() {
    this.rules = [];
    this._rulesSubject.next(this.rules);
  }
  setPath(path: any) {
    this._pathSubject.next(path);
  }
  getPath() {
    return this._pathSubject.asObservable();
  }
  setPopUpValueForExpressionBuilder(isExpressionBuilderPopUp: boolean) {
    this._isExpressionBuilderPopUpSubject.next(isExpressionBuilderPopUp);
  }
  $getPopUpValueForExpressionBuilder =
    this._isExpressionBuilderPopUpSubject.asObservable();

  getMethodParameters(methodName: string): Observable<ReturnResponse<REMethodParameters[]>>{
    return this._httpClient.get<ReturnResponse<REMethodParameters[]>>(`${this.apiURL}api/RuleEngineReports/GetMethodParameters?methodName=${methodName}`)
  }
  getMethodNames() : Observable<ReturnResponse<REMethodNames>>{
    return this._httpClient.get<ReturnResponse<REMethodNames>>(`${this.apiURL}api/RuleEngineReports/GetMethodNames`)
  }
  getCoreCache(body){
  return this._httpClient.post<any>(`${this.apiURL}api/CoreCache/GetAllCache`,body,this.header);
}
getClearCache(input:any){
  return this._httpClient.post<any>(`${this.apiURL}api/CoreCache/Deletecorecache?keys=${input}`,this.header);
}
}
